import type { LucideIcon } from "lucide-react";
import { CourseMarkdown } from "@/components/storyboard/CourseMarkdown";
import { CodeBlock } from "@/components/storyboard/code/CodeBlock";
import { deriveDefaultLanguage } from "@/components/storyboard/code/codeUtils";
import { splitLeadingHeading } from "@/components/storyboard/text/utils/splitLeadingHeading";
import ContentBlock from "@/components/storyboard/ContentBlock";
import type { CodeExampleData } from "@/types/storyboard";

interface CodeExampleProps {
  data: CodeExampleData;
  icon?: LucideIcon;
  title?: string;
  numberSnippets?: boolean;
}

export const CodeExample = ({
  data,
  icon,
  title,
  numberSnippets = true,
}: CodeExampleProps) => {
  const defaultLanguage = deriveDefaultLanguage(
    (data.sections ?? [])
      .map((section) => section.sourceCode)
      .filter((sourceCode): sourceCode is string => Boolean(sourceCode))
  );

  let exampleCount = 0;
  const sections = (data.sections ?? []).map((section) => ({
    ...splitLeadingHeading(section.text ?? ""),
    sourceCode: section.sourceCode,
    exampleNumber: section.sourceCode ? ++exampleCount : 0,
  }));

  // A titled example shows that title alone: the first section's heading was
  // consumed into `sections` and would otherwise head the block twice. An
  // untitled one is headed by that scraped heading instead.
  const blockTitle = title ?? data.title?.trim() ?? sections[0]?.title;

  // Only the problem-solving variant keeps its first heading in place, where
  // it names a step rather than the block.
  const firstSectionKeepsItsHeading = Boolean(title);

  // One labeled unit per content element: the header (title) renders once,
  // sections stack inside it.
  return (
    <ContentBlock icon={icon} title={blockTitle ?? undefined}>
      {sections.map((section, sectionIndex) => (
        <section
          key={sectionIndex}
          className={sectionIndex === 0 ? "" : "mt-8"}
        >
          {(sectionIndex > 0 || firstSectionKeepsItsHeading) &&
            section.title && <h3 className="mb-4">{section.title}</h3>}

          {section.body && (
            <div className="prose max-w-full text-primary mb-6">
              <CourseMarkdown>{section.body}</CourseMarkdown>
            </div>
          )}

          {section.sourceCode && (
            <div className="bg-white rounded-xl border border-primary/20 overflow-hidden">
              {numberSnippets && (
                <div className="px-4 py-2 border-b border-primary/20">
                  <span className="text-xs font-semibold text-primary/70 uppercase tracking-wide">
                    Beispiel {section.exampleNumber}
                  </span>
                </div>
              )}
              <div className="p-4">
                <CodeBlock
                  code={section.sourceCode}
                  defaultLanguage={defaultLanguage}
                />
              </div>
            </div>
          )}
        </section>
      ))}
    </ContentBlock>
  );
};
